import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Switch } from "../../../ui/switch";
// import { Checkbox } from '../../../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  // Search,
  Plus,
  Edit2,
  Trash2,
  Route,
  X,
  Filter,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { AtraafItem, UpdateAtraafStatusPayload, Venue } from "types/ziyarat";
import { useScreenPermission } from "hooks/useScreenPermission";
import useDebounce from "hooks/useDebounce";
import { FullScreenLoading } from "components/ui/loader";
import {
  deleteAtraaf,
  getAtraafList,
  getVenuesList,
  UpdateAtraafStatus,
} from "services/ziyaratService";
import Pagination from "ui/pagination";
import ConfirmDialog from "ui/ConfirmDialog";
import { usePageHeader } from "contexts/PageHeaderContext";

interface ZiyaratRoutesMasterPageProps {
  onNavigate?: (page: string) => void;
}

export const ZiyaratAtraafRoutesMaster = ({
  onNavigate,
}: ZiyaratRoutesMasterPageProps = {}) => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { canAdd, canEdit, canDelete } = useScreenPermission();
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [atraafList, setAtraafList] = useState<AtraafItem[]>([]);
  const [atraaftotalCount, setAtraafTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [atraafDeleteId, setAtraafDeleteId] = useState<string | null>(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AtraafItem | null>(null);
  const [venuesOptions, setVenuesOptions] = useState<Venue[]>([]);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Filter states
  const [filters, setFilters] = useState({
    routeName: "",
    venue: "",
    transport: "",
    type: "", // mandatory or optional
    status: "",
    lawazimRequired: "",
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      searchTerm,
    }),
    [filters, searchTerm],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);

  useEffect(() => {
    setPageHeader({
      title: "Atraaf",
      childTitle: "Manage ziyarat routes with multiple stops",
      icon: <Route className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const getVenuesListApi = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVenuesList({ onlyActive: true });
      setVenuesOptions(data);
    } catch {
      toast.error("Failed to fetch venues");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getVenuesListApi();
  }, [getVenuesListApi]);

  const fetchAtraafList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAtraafList(debouncedFilters);
      setAtraafList(data.items);
      setAtraafTotalCount(data.totalCount);
    } catch {
      toast.error("Failed to fetch Ziyarat");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchAtraafList();
  }, [fetchAtraafList]);

  // const handleSort = (column: string) => {
  //   if (sortColumn === column) {
  //     setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortColumn(column);
  //     setSortDirection('asc');
  //   }
  // };

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedApplicants = useMemo(() => {
    if (!sortColumn) return atraafList;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...atraafList].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" },
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [atraafList, sortColumn, sortDirection]);

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3.5 h-3.5 ml-1 inline" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="w-3.5 h-3.5 ml-1 inline" />
    ) : (
      <ChevronDown className="w-3.5 h-3.5 ml-1 inline" />
    );
  };

  const resetFilters = () => {
    setFilters({
      routeName: "",
      venue: "",
      transport: "",
      type: "", // mandatory or optional
      status: "",
      lawazimRequired: "",
      pageNo: 1,
      pageSize: 20,
    });
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.routeName !== "" ||
    filters.venue !== "" ||
    filters.transport !== "" ||
    filters.type !== "" ||
    filters.status !== "" ||
    filters.lawazimRequired !== "";

  // STATUS CHANGE FUNCTIONALITY START
  const toggleStatus = (item: AtraafItem) => {
    setSelectedItem(item);
    setIsStatusDialogOpen(true);
  };

  const cancelStatusChange = () => {
    setSelectedItem(null);
    setIsStatusDialogOpen(false);
  };

  const confirmStatusChange = async () => {
    if (!selectedItem) return;
    try {
      const payload: UpdateAtraafStatusPayload = {
        publicId: String(selectedItem.publicId),
        isActive: !selectedItem.isActive,
      };
      await UpdateAtraafStatus(payload);
      //UPDATE LOCAL STATE INSTEAD OF CALLING API AGAIN
      setAtraafList((prev) =>
        prev.map((item) =>
          item.publicId === selectedItem.publicId
            ? { ...item, isActive: !item.isActive }
            : item,
        ),
      );
      toast.success(
        `Ziyarat atraaf ${selectedItem.isActive ? "deactivated" : "activated"} successfully`,
      );
      // fetchAtraafList();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    } finally {
      setIsStatusDialogOpen(false);
      setSelectedItem(null);
    }
  };
  // STATUS CHANGE FUNCTIONALITY END

  // DELETE ZIYARAT FUNCTIONLITY START
  const handleDeleteAtraaf = (id: string) => {
    setAtraafDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const cancelDeleteDraft = () => {
    setDeleteDialogOpen(false);
    setAtraafDeleteId(null);
  };

  const confirmDeleteAtraaf = async () => {
    if (!atraafDeleteId) return;
    try {
      setLoading(true);
      await deleteAtraaf(atraafDeleteId);
      toast.success("Ziyarat atraaf deleted successfully");
      await fetchAtraafList();
      setDeleteDialogOpen(false);
      setAtraafDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };
  // DELETE ZIYARAT FUNCTIONLITY END

  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {/* Header */}
      <div className="flex justify-end flex-col sm:flex-row sm:items-center gap-4">
        {canAdd && (
          <Button
            onClick={() => navigate("/atraaf/add")}
            style={{ background: "#5B9BD5" }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Atraaf
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card className="border-[#5B9BD5]/30">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <Input
                placeholder="Search by atraaf name, venue..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setFilters((prev) => ({ ...prev, pageNo: 1 }));
                }}
                className="pl-9 sm:pl-10 text-sm sm:text-base h-9 sm:h-10"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 w-full sm:w-10 h-9 sm:h-10 p-0"
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
              <span className="sm:hidden ml-2">Advanced Filters</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters */}
      {showFilters && (
        <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="flex items-center gap-2 text-lg"
                >
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <CardDescription className="mt-1">
                  Filter routes by multiple criteria
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Route Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-route" className="text-sm">
                  Atraaf Name
                </Label>
                <Input
                  id="filter-route"
                  placeholder="Search by name..."
                  value={filters.routeName}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      routeName: e.target.value,
                      pageNo: 1,
                    })
                  }
                  className="h-9 text-sm"
                />
              </div>

              {/* Venue Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-venue" className="text-sm">
                  Venue
                </Label>
                <Select
                  value={filters.venue}
                  onValueChange={(value) =>
                    setFilters({ ...filters, venue: value, pageNo: 1 })
                  }
                >
                  <SelectTrigger id="filter-venue" className="h-9 text-sm">
                    <SelectValue placeholder="All venues" />
                  </SelectTrigger>
                  <SelectContent>
                    {venuesOptions.map((venue) => (
                      <SelectItem
                        key={venue.venueId}
                        value={venue.venueId.toString()}
                      >
                        {venue.venueName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Transport Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-transport" className="text-sm">
                  Transport Required
                </Label>
                <Select
                  value={filters.transport}
                  onValueChange={(value) =>
                    setFilters({ ...filters, transport: value, pageNo: 1 })
                  }
                >
                  <SelectTrigger id="filter-transport" className="h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Required</SelectItem>
                    <SelectItem value="false">Not Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Type Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-type" className="text-sm">
                  Route Type
                </Label>
                <Select
                  value={filters.type}
                  onValueChange={(value) =>
                    setFilters({ ...filters, type: value, pageNo: 1 })
                  }
                >
                  <SelectTrigger id="filter-type" className="h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Mandatory</SelectItem>
                    <SelectItem value="false">Optional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-status" className="text-sm">
                  Status
                </Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) =>
                    setFilters({ ...filters, status: value, pageNo: 1 })
                  }
                >
                  <SelectTrigger id="filter-status" className="h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active Only</SelectItem>
                    <SelectItem value="false">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Lawazim Required Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-lawazim" className="text-sm">
                  Lawazim Required
                </Label>
                <Select
                  value={filters.lawazimRequired}
                  onValueChange={(value) =>
                    setFilters({
                      ...filters,
                      lawazimRequired: value,
                      pageNo: 1,
                    })
                  }
                >
                  <SelectTrigger id="filter-lawazim" className="h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Summary & Clear Button */}
            {hasActiveFilters && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                    Active Filters:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.routeName && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                      >
                        Atraaf: {filters.routeName}
                      </Badge>
                    )}
                    {filters.venue !== "" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                      >
                        Venue:{" "}
                        {
                          venuesOptions.find(
                            (item) =>
                              String(item.venueId) === String(filters.venue),
                          )?.venueName
                        }
                      </Badge>
                    )}
                    {filters.transport !== "" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                      >
                        Transport:{" "}
                        {filters.transport === "true"
                          ? "Required"
                          : "Not Required"}
                      </Badge>
                    )}
                    {filters.type !== "" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                      >
                        Route Type:{" "}
                        {filters.type === "true" ? "Mandatory" : "Optional"}
                      </Badge>
                    )}
                    {filters.status !== "" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                      >
                        Status:{" "}
                        {filters.status === "true" ? "Active" : "InActive"}
                      </Badge>
                    )}
                    {filters.lawazimRequired !== "" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                      >
                        Lawazim Required:{" "}
                        {filters.lawazimRequired === "true" ? "Yes" : "No"}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="bg-white border-[#5B9BD5]/30">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-xl">
          <CardTitle style={{ color: "#5B9BD5" }}>Atraaf</CardTitle>
          <CardDescription>
            {atraafList.length} route{atraafList.length !== 1 ? "s" : ""} found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer hover:bg-[#5B9BD5]/10"
                    onClick={() => handleSort("atraafName")}
                  >
                    Atraaf Name {getSortIcon("atraafName")}
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer hover:bg-[#5B9BD5]/10"
                    onClick={() => handleSort("venueName")}
                  >
                    Venue {getSortIcon("venueName")}
                  </TableHead>

                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer hover:bg-[#5B9BD5]/10"
                    onClick={() => handleSort("venueName")}
                  >     
                    Capacity{getSortIcon("capacity")}
                  </TableHead>

                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer hover:bg-[#5B9BD5]/10"
                    onClick={() => handleSort("detailsCount")}
                  >
                    Stops {getSortIcon("detailsCount")}
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-center cursor-pointer hover:bg-[#5B9BD5]/10"
                    onClick={() => handleSort("transportRequired")}
                  >
                    Transport {getSortIcon("transportRequired")}
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-center cursor-pointer hover:bg-[#5B9BD5]/10"
                    onClick={() => handleSort("isActive")}
                  >
                    Status {getSortIcon("isActive")}
                  </TableHead>
                  {(canEdit || canDelete) && (
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {atraafList.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 px-4 text-gray-500 text-sm"
                    >
                      No routes found
                    </TableCell>
                  </TableRow>
                ) : (
                  atraafList &&
                  sortedApplicants?.map((item, index) => (
                    <TableRow key={index} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                            <Route className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {item.atraafName}
                            {!item.optionalRoute && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          {item.venueName}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className="bg-yellow-100 border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          {item?.capacity}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className="bg-gray-50 border-gray-300 text-gray-700 text-xs"
                        >
                          {item.detailsCount} stop
                          {item.detailsCount !== 1 ? "s" : ""}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        {item.transportRequired ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                            Yes
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="bg-gray-50 text-gray-600 text-xs"
                          >
                            No
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center">
                          <Switch
                            checked={item.isActive}
                            onCheckedChange={() => toggleStatus(item)}
                            className="data-[state=checked]:bg-[#5B9BD5] scale-75"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                navigate(`/atraaf/edit/${item.publicId}`)
                              }
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                handleDeleteAtraaf(String(item.publicId))
                              }
                              className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3 p-4">
            {atraafList.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Route className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No routes found</p>
              </div>
            ) : (
              atraafList.map((item) => (
                <Card
                  key={item.publicId}
                  className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center">
                          <Route className="w-5 h-5 text-[#5B9BD5]" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            {item.atraafName}
                            {!item.optionalRoute && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </p>
                          <div className="flex gap-1 mt-1">
                            <Badge
                              variant="outline"
                              className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                            >
                              {item.venueName}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-xs mb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Stops:</span>
                        <Badge
                          variant="outline"
                          className="bg-gray-50 border-gray-300 text-gray-700 text-xs"
                        >
                          {item.detailsCount} stop
                          {item.detailsCount !== 1 ? "s" : ""}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Transport:</span>
                        {item.transportRequired ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                            Yes
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="bg-gray-50 text-gray-600 text-xs"
                          >
                            No
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={item.isActive}
                          onCheckedChange={() => toggleStatus(item)}
                          className="data-[state=checked]:bg-[#5B9BD5]"
                        />
                        <span
                          className={`text-xs font-medium ${item.isActive ? "text-green-700" : "text-gray-600"}`}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            navigate(`/atraaf/edit/${item.publicId}`)
                          }
                          className="h-8 w-8 text-xs"
                        >
                          <Edit2 className="w-3 h-3 mr-1" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleDeleteAtraaf(String(item.publicId))
                          }
                          className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          {/* PAGINATION COMPONENT FOR DRAFT */}
          <Pagination
            totalItems={atraaftotalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((prev) => ({
                ...prev,
                pageNo: page, // convert back to 0-based
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1, // IMPORTANT: reset page
              }))
            }
            label=""
          />
        </CardContent>
      </Card>

      {/* STATUS CHANGE CONFIRMATION POPUP */}
      <ConfirmDialog
        show={false}
        open={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        title="Confirm Status Change"
        description={`Are you sure you want to ${
          selectedItem?.isActive ? "deactivate" : "activate"
        } this place?`}
        confirmText="Yes, Confirm"
        onConfirm={confirmStatusChange}
        onCancel={cancelStatusChange}
      />

      {/* DELETE CONFIRMATION POPUP */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Confirm Delete"
        description="Are you sure you want to delete?"
        confirmText="Yes, Delete"
        // loading={isDeleting}
        onConfirm={confirmDeleteAtraaf}
        onCancel={cancelDeleteDraft} // ✅ NOW IT WORKS
      ></ConfirmDialog>
    </div>
  );
};
